import hotkeys from 'hotkeys-js';
import { type Dispatch, type KeyboardEvent, type SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHotKeyScope } from 'reactApp/hooks/useHotkeyScope';
import { HOT_KEY_SCOPE_NAME, MAX_PROMPT_DWH_SYMBOLS } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.constants';
import {
    getAiGenerationImageSize,
    getAiGenerationPrompt,
    getAiGenerationStyle,
    getAiGenerationTime,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.selectors';

export const useAiPhotoGenerationAnalyticData = () => {
    const prompt = useSelector(getAiGenerationPrompt);
    const style = useSelector(getAiGenerationStyle);
    const time = useSelector(getAiGenerationTime);
    const size = useSelector(getAiGenerationImageSize);

    return useMemo(
        () => ({
            generation_prompt: prompt?.slice(0, MAX_PROMPT_DWH_SYMBOLS),
            generation_size: size?.space,
            generation_style: style,
            generation_time: time,
        }),
        [prompt, size?.space, style, time]
    );
};

interface IUseAiPhotoGenerationPromptKeyDown {
    handleStartGeneration: () => void;
    setValue: Dispatch<SetStateAction<string>>;
    onPromptChange: (value: string) => void;
}

export const useAiPhotoGenerationPromptKeyDown = ({
    handleStartGeneration,
    setValue,
    onPromptChange,
}: IUseAiPhotoGenerationPromptKeyDown) => {
    useHotKeyScope(HOT_KEY_SCOPE_NAME);

    useEffect(() => {
        hotkeys('enter', HOT_KEY_SCOPE_NAME, handleStartGeneration);

        return () => {
            hotkeys.unbind('enter', HOT_KEY_SCOPE_NAME, handleStartGeneration);
        };
    }, [handleStartGeneration]);

    return useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key !== 'Enter') {
                return;
            }

            // Shift+Enter — браузер сам вставит перевод строки
            if (event.shiftKey) {
                return;
            }

            // Cmd/Ctrl+Enter — браузер перевод строки не вставляет, делаем вручную
            if (event.metaKey || event.ctrlKey) {
                event.preventDefault();
                const textarea = event.currentTarget;
                // setRangeText сам вставит \n и поставит каретку в конец вставки
                textarea.setRangeText('\n', textarea.selectionStart, textarea.selectionEnd, 'end');
                // input-событие при этом не диспатчится — синхронизируем стейт вручную
                setValue(textarea.value);
                onPromptChange(textarea.value);
                return;
            }

            // чистый Enter — запуск генерации
            event.preventDefault();
            handleStartGeneration();
        },
        [handleStartGeneration, setValue, onPromptChange]
    );
};
